/*
 * ItemCombobox.tsx - Eine Entity (zB ItemNo) aus der Suche auswählen
 *
 * Created by Dr. Maximillian Dornseif 2022-02-10 in huWaWi3 25.2.0
 * Copyright (c) 2022, 2023  Dr. Maximillian Dornseif
 */

import { findAllByKey } from '@hudora/hd-ts-util'
import Autocomplete from '@mui/material/Autocomplete'
import CircularProgress from '@mui/material/CircularProgress'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import TextField from '@mui/material/TextField'
import { assertIsString } from 'assertate-debug'
import { DocumentNode } from 'graphql'
import React, { useCallback, useEffect, useState } from 'react'
import { useQuery } from 'urql'

import { renderTemplateString } from '../../lib/render-template-string/render-template-string'
import { fuzzaldrinHighlight, fuzzaldrinSort } from '../../lib/util/fuzzaldrin-react'

export interface IEntitySelectComboboxStaticProps {
  query: DocumentNode
  variables: Record<string, any>
  name?: string
  disabled?: boolean
  placeholder?: string
  onChange?
  onBlur?
  onChanged?: (str) => void
  /**
   * An TextField.label weitergegeben
   */
  label?: string
  /**
   * An TextField.variant weitergegeben
   */
  variant?
  /**
   * An TextField.error weitergegeben
   */
  error?: boolean
  /**
   * An TextField.helperText weitergegeben
   */
  helperText?: string
}

export function EntitySelectComboboxStatic(props: IEntitySelectComboboxStaticProps) {
  const [rowData, setRowData] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [result] = useQuery({
    query: props.query,
    variables: props.variables,
    pause: props.disabled,
  })
  const { data, fetching } = result

  useEffect(() => {
    // eslint-disable-next-line no-template-curly-in-string
    setRowData(
      findAllByKey(data, 'nodes')
        .map((row) => {
          return {
            ...row,
            title: renderTemplateString(
              // eslint-disable-next-line no-template-curly-in-string
              '${designator} ${plz} ${ort}, ${strasse} ${land} ${iln}',
              row
            ).replace('null', ' '),
          }
        })
        .filter((row) => row.ist_aktiv)
    )
  }, [data, setRowData])

  const options = inputValue && inputValue.length > 3 ? fuzzaldrinSort(inputValue, rowData) : rowData

  const handleOnChange = useCallback(
    (event: any, newValue: any) => {
      assertIsString(newValue.designator)
      if (props?.onChanged) {
        props?.onChanged(newValue.designator)
      }

      // To use the Event in react-hook-form name has to be set as expected by react-hook-form
      event.target.name = props.name

      // We also have to make sure, that `designator` and not `title` is sent upstream.
      event.target.value = newValue.designator
      if (props?.onChange) {
        props?.onChange(event)
      }
    },
    [props?.onChanged, props.name, props?.onChange]
  )

  // see https://mui.com/api/autocomplete/
  return (
    <Autocomplete
      sx={{ width: '25em' }}
      isOptionEqualToValue={(option, v) => option?.designator === v.designator}
      getOptionLabel={(option) => option?.title}
      filterOptions={(x) => x} // filtering disabled
      options={options}
      disabled={!!props.disabled}
      loading={fetching}
      // loadingText
      autoComplete
      autoHighlight
      autoSelect
      ListboxComponent={List}
      // ListboxProps={{ dense: true } as any}
      // noOptionsText={
      //   inputValue.length < MIN_INPUT_LENGTH
      //     ? 'bitte mindestens 3 Zeichen eingeben'
      //     : '(noch) keine Ergebnisse'
      // }
      onChange={handleOnChange}
      // onBlur={(event: any) => {
      //   // To use the Event in react-hook-form name has to be set as expected by react-hook-form
      //   event.target.name = props.name
      //   event.target.type = 'input'
      //   // We also have to make sure, that `designator` and not `title` is sent upstream.
      //   event.target.value = event.target.value.split(' ')?.[0]
      //   console.log(event.target.value)
      //   if (props?.onChange) {
      //     props?.onBlur(event)
      //   }
      // }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue)
      }}
      renderInput={(params) => (
        <TextField
          variant={props.variant || 'standard'}
          error={props.error}
          helperText={props.helperText}
          {...params}
          label={props.label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {fetching ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      renderOption={(props, option, state) => {
        return (
          <ListItem key={`${option.designator}`} {...props}>
            <ListItemText
              sx={{
                textDecoration:
                  option.istAktiv === false || option.ist_aktiv === false ? 'line-through' : 'none',
              }}
              primary={fuzzaldrinHighlight(option.title, inputValue)}
            />
          </ListItem>
        )
      }}
    />
  )
}
